/**
 * Centralized API configuration
 * All API calls should use this instance to ensure credentials are included
 */
import axios from 'axios';

// Get configured backend URL
let BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ==================== PRODUCTION GUARD ====================
// CRITICAL: If running on sacredsteel.app, FORCE correct backend URL
// This prevents misconfigured builds from breaking production
const currentHostname = typeof window !== 'undefined' ? window.location.hostname : '';

if (currentHostname === 'sacredsteel.app') {
  // Force production URL regardless of build config
  if (BACKEND_URL !== 'https://sacredsteel.app') {
    console.error('[API] CRITICAL MISCONFIGURATION DETECTED!');
    console.error(`[API] Running on sacredsteel.app but REACT_APP_BACKEND_URL=${BACKEND_URL}`);
    console.error('[API] FORCING correct URL: https://sacredsteel.app');
    BACKEND_URL = 'https://sacredsteel.app';
  }
  
  // Hard block any emergent.host or emergentagent.com URLs in production
  if (BACKEND_URL.includes('emergent.host') || BACKEND_URL.includes('emergentagent.com')) {
    throw new Error(
      `FATAL: Production build misconfigured! ` +
      `Backend URL "${BACKEND_URL}" is invalid for sacredsteel.app. ` +
      `Must use https://sacredsteel.app`
    );
  }
}

// Log backend URL on startup for debugging
console.log(`[API] Backend URL configured: ${BACKEND_URL || '(empty - using relative URLs)'}`);
console.log(`[API] Current hostname: ${currentHostname}`);
console.log(`[API] Environment: ${process.env.NODE_ENV}`);

// Warn if using preview/staging URL
if (BACKEND_URL.includes('emergent.host') || BACKEND_URL.includes('emergentagent.com')) {
  console.warn('[API] WARNING: Using preview/staging URL. Production should use sacredsteel.app');
}

// Create axios instance with default config
const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true, // CRITICAL: Include cookies in all requests
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    // Production guard: Block any requests to wrong domains
    const fullUrl = config.baseURL + config.url;
    if (currentHostname === 'sacredsteel.app') {
      if (fullUrl.includes('emergent.host') || fullUrl.includes('emergentagent.com')) {
        console.error('[API] BLOCKED: Attempted request to wrong domain:', fullUrl);
        return Promise.reject(new Error('Blocked: Cannot make requests to preview URLs from production'));
      }
    }
    console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('[API] Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.code === 'ERR_NETWORK') {
      console.error('[API] Network error - check CORS and backend connectivity');
    } else if (error.response?.status === 401) {
      console.log('[API] Unauthorized - session may have expired');
    }
    return Promise.reject(error);
  }
);

// Export both the instance and the base URL for cases where raw URL is needed
export const API_BASE_URL = `${BACKEND_URL}/api`;
export const BACKEND_BASE_URL = BACKEND_URL;
export default api;
